import { useCallback, useMemo } from 'react'

import { format, isToday, isYesterday } from 'date-fns'
import { useUnit } from 'effector-react'

import CloseIcon from '@/assets/icons/icon-close.svg'
import { SafeFormattedText } from '@/components/ui/safe-formatted-message'
import { Separator } from '@/components/ui/separator.tsx'
import { $activeChatDetails, $fanId } from '@/models/chattingUI/chats/chats.ts'
import { useChatting } from '@/modules/chatting/state/chatting-provider.tsx'

import { ChatMediaCounter } from './chat-media-counter'

export const ReplyToMessageContainer = () => {
  const { replyToMessage, setReplyToMessage } = useChatting()
  const fanId = useUnit($fanId)

  const activeChat = useUnit($activeChatDetails)
  const { fromUser, createdAt, text, authorName } = replyToMessage ?? {}

  const createdAtDate = useMemo(
    () => (createdAt ? new Date(createdAt) : undefined),
    [createdAt],
  )

  const isDateToday = useMemo(
    () => createdAtDate && isToday(createdAtDate),
    [createdAtDate],
  )
  const isDateYesterday = useMemo(
    () => createdAtDate && isYesterday(createdAtDate),
    [createdAtDate],
  )
  const formattedDate = useMemo(() => {
    if (createdAtDate) {
      if (isDateToday) {
        return `Today ${format(createdAtDate, 'hh:mm a')}`
      }

      if (isDateYesterday) {
        return `Yesterday ${format(createdAtDate, 'hh:mm a')}`
      }

      return format(createdAtDate, 'hh:mm a')
    }
  }, [createdAtDate, isDateToday, isDateYesterday])

  const isFromPartner = fromUser?.id === fanId

  const handleClose = useCallback(() => {
    setReplyToMessage(undefined)
  }, [setReplyToMessage])

  if (!replyToMessage) {
    return null
  }

  return (
    <div className="bg-primary-100 dark:bg-primary-900 flex justify-between rounded p-2">
      <div className="max-w-full pr-2 lg:max-w-[50%]">
        <p className="text-xs">
          <span className="font-medium">
            {isFromPartner
              ? activeChat?.withUser?.name
              : (authorName ?? 'Unknown')}
          </span>
          , <span className="text-secondary-300">{formattedDate}</span>
        </p>
        {replyToMessage.media && (
          <div className="my-2">
            <ChatMediaCounter message={replyToMessage} />
          </div>
        )}
        {text && (
          <>
            <Separator className="my-1" />
            <p className="line-clamp-4 overflow-hidden text-sm">
              {text && (
                <SafeFormattedText
                  text={text}
                  plain
                />
              )}
            </p>
          </>
        )}
      </div>

      <CloseIcon
        className="hover:text-primary-500 cursor-pointer transition"
        onClick={handleClose}
      />
    </div>
  )
}
